import { useEffect } from 'react'
import type { GetServerSidePropsContext } from 'next'
import dynamic from 'next/dynamic'
import { BogoBanner, BogoPromoModal } from '@mc/bogo'
import { PAGE_SOURCES, PromoExplainerModal, DiscountModal } from '@mc/promos'
import { StaticDataProvider } from '@mc/static-data-react'
import { TRACK_PROPS, trackPage } from '@mc/track-event'
import { TrackingParamsProvider } from '@mc/tracking-context'
import { RegularFooter } from 'components/Footer/RegularFooter'
import GuestPassBanner from 'components/GuestPassBanner'
import { CoreNavigation } from 'components/Navigation/consumer'
import { StickyFooter } from 'components/StickyFooter'
import {
  SkinnyCmModalGate,
  useTrackPen6957Experiment,
} from 'experiments/pen-6957_v2_cm_skinny_cm'
import { trackPen6957BackendEV } from 'experiments/pen-6957_v2_cm_skinny_cm/server'
import { InstructorQuoteSection } from 'experiments/pen-7315_v1_cm_instructor_quote'
import { getEligibleInstructorQuote } from 'experiments/pen-7315_v1_cm_instructor_quote/server'
import {
  Pen7395BelowHeroWrapper,
  useTrackPen7395Experiment,
} from 'experiments/pen-7395_v2_cm_hero_only'
import { trackPen7395BackendEV } from 'experiments/pen-7395_v2_cm_hero_only/server'
import {
  Pen7401CategoryPicker,
  useTrackPen7401Experiment,
} from 'experiments/pen-7401_v1_cm_category_picker'
import {
  getPen7401MarketingRows,
  trackPen7401BackendEV,
} from 'experiments/pen-7401_v1_cm_category_picker/server'
import { useTrackPen7562Experiment } from 'experiments/pen-7562_v1_cm_promo_discount_guarantee'
import { trackPen7562BackendEV } from 'experiments/pen-7562_v1_cm_promo_discount_guarantee/server'
import { trackPen7565BackendEV } from 'experiments/pen-7565_v1_promo_banner_200_classes/server'
import { trackPen7584BackendEV } from 'experiments/pen-7584_v1_promo_banner_all_classes/server'
import { trackPen7659BackendEV } from 'experiments/pen-7659_v1_cm_start_your_journey_gift_cta/server'
import { trackPen7680BackendEV } from 'experiments/pen-7680_v1_promo_banner_gift_cta/server'
import { trackPen7694BackendEV } from 'experiments/pen-7694_v1_promo_sticky_footer_gift_cta_routing/server'
import { useTrackPrcng1266Experiment } from 'experiments/prcng-1266_v1_cm_dual_cta'
import { useTrackPrcng1270Experiment } from 'experiments/prcng-1270_v1_mc_pro_promo'
import { trackPrcng1270BackendEV } from 'experiments/prcng-1270_v1_mc_pro_promo/server'
import { ssrDynamicHero } from 'libs/dynamic-hero'
import { trackProductViewedCourse } from 'libs/events'
import { getLcmCtaOverride } from 'libs/lcm/getLcmCtaOverride'
import {
  getV2Categories,
  getCourseMeta,
  getFreeTrialByToken,
  getDefaultProduct,
  getMobileContentGems,
  getCourseLegacyTaxonomy,
} from 'libs/server/api'
import {
  getGrowthClassCategories,
  getGrowthClassesBySubcategoryIds,
  getGrowthClassSubcategories,
  getPrimaryCategoryAndSubcategoryForGrowthClass,
} from 'libs/server/api/growth'
import { getPageData } from 'libs/server/page-data'
import { pageErrorHandler } from 'libs/server/page-error-handler'
import { transformCourseRedirect } from 'libs/server/redirect'
import { useSprig } from 'libs/sprig'
import { redirect } from 'libs/utils'
import { getSSRQuizOnlyOnceCookie } from 'libs/utils/quizOnlyOnce'
import { Hero } from './components/Hero'
import Meta from './components/Meta'
import { TrailerDeeplinkGate } from './components/TrailerDeeplinkGate'
import { SkillsYoullLearnSection } from './components/sections'
import { trackViewedClass } from './events'
import type { CoursePageProps } from './types'

const BelowTheFold = dynamic(
  () => import('./components/BelowTheFold').then((m) => m.BelowTheFold),
  {
    ssr: false,
  },
)

const Course = ({
  course,
  metadata,
  trial,
  primaryGrowthCatAndSubcat,
  annualPass,
  categories,
  savedDynamicHero,
  ctaNextPage,
  classSubcategories,
  otherClassesInSameSubcategories,
  classCategories,
  mobileContentGems,
  marketingRows, // pen-7401_v1_cm_category_picker
  instructorQuote, // pen-7315_v1
}: CoursePageProps) => {
  useEffect(() => {
    if (annualPass) {
      trackProductViewedCourse({
        annualPass,
        course,
        coursePrimaryCategory: primaryGrowthCatAndSubcat.primaryCategory,
        courseCategoryIds: classCategories.map((cat) => cat.id),
        primarySubcategory: primaryGrowthCatAndSubcat?.primarySubcategory,
        courseSubcategoriesIds: classSubcategories.map((subcat) => subcat.id),
      })
    }
  }, [
    course,
    primaryGrowthCatAndSubcat,
    annualPass,
    classSubcategories,
    classCategories,
  ])

  useEffect(() => {
    trackPage('Course Marketing', {
      saved_completed_quiz: !!ctaNextPage,
      platform: 'next',
    })
  }, [course.slug, ctaNextPage])

  useTrackPen6957Experiment(course.slug) // pen-6957_v2_cm_skinny_cm
  useTrackPen7395Experiment(course.slug) // pen-7395_v2_cm_hero_only
  useTrackPen7401Experiment() // pen-7401_v1_cm_category_picker
  useTrackPen7562Experiment() // pen-7562_v1_cm_promo_discount_guarantee
  useTrackPrcng1266Experiment() // prcng-1266_v1_cm_dual_cta
  useTrackPrcng1270Experiment() // prcng-1270_v1_mc_pro_promo
  useSprig()

  return (
    <TrackingParamsProvider value={{ [TRACK_PROPS.PAGE_TYPE]: 'next-classes' }}>
      <StaticDataProvider
        value={{
          annualPass,
          course,
          trial,
          metadata,
          primaryGrowthCatAndSubcat,
          categories,
          savedDynamicHero,
          ctaNextPage,
          classSubcategories,
          otherClassesInSameSubcategories,
          classCategories,
          mobileContentGems,
          marketingRows, // pen-7401_v1_cm_category_picker
          instructorQuote, // pen-7315_v1
        }}
        providerKey={course.slug}
      >
        <Meta />
        {/* Modals */}
        <BogoPromoModal />
        <PromoExplainerModal pageSource={PAGE_SOURCES.CM_PAGE} />
        <DiscountModal />
        <SkinnyCmModalGate /> {/* pen-6957_v2_cm_skinny_cm */}
        <TrailerDeeplinkGate />
        {/* Banners */}
        <BogoBanner />
        <GuestPassBanner />
        {/* Page Content */}
        <Hero />
        <Pen7395BelowHeroWrapper
          Control={
            <>
              <InstructorQuoteSection /> {/* pen-7315_v1 */}
              <Pen7401CategoryPicker />
              <SkillsYoullLearnSection />
              <BelowTheFold />
            </>
          }
        />
        <StickyFooter />
      </StaticDataProvider>
    </TrackingParamsProvider>
  )
}

Course.pageSettings = {
  showPromo: true,
  // analyticsLabel intentionally missing: trackPage is called above with extra attributes (quiz only once)
  navigation: CoreNavigation,
  footer: RegularFooter,
}

export async function getServerSideProps(ctx: GetServerSidePropsContext) {
  const {
    query: { slug: slugQuery },
    req: {
      cookies: { referral_token: referralToken },
    },
    resolvedUrl,
  } = ctx
  if (typeof slugQuery !== 'string') {
    return pageErrorHandler(new Error('Invalid slug'))
  }

  let course
  try {
    course = await getCourseLegacyTaxonomy(
      ctx,
      { slug: slugQuery },
      { withAspectRatios: true }, // PEN-6909: hero reserves nameplate space
    )
  } catch (err) {
    return pageErrorHandler(err)
  }

  const { slug } = course
  if (slug !== slugQuery) {
    return redirect(ctx, `/classes/${slug}`, { queryExclude: ['slug'] })
  }

  trackViewedClass(ctx.req, ctx.res, slug)

  const { savedDynamicHero } = await ssrDynamicHero(ctx, slug)

  const primaryGrowthCatAndSubcat =
    await getPrimaryCategoryAndSubcategoryForGrowthClass(ctx, slug)

  const ctaNextPage = await getSSRQuizOnlyOnceCookie(ctx)

  const primaryCtaOverride = getLcmCtaOverride(ctx, slug)

  const classSubcategories = await getGrowthClassSubcategories(ctx, slug)

  await Promise.all([
    trackPen6957BackendEV(ctx), // pen-6957_v2_cm_skinny_cm
    trackPen7395BackendEV(ctx, slug),
    trackPen7401BackendEV(ctx),
    trackPen7562BackendEV(ctx), // pen-7562_v1_cm_promo_discount_guarantee
    trackPen7565BackendEV(ctx),
    trackPen7584BackendEV(ctx),
    trackPen7659BackendEV(ctx),
    trackPen7680BackendEV(ctx),
    trackPen7694BackendEV(ctx),
    trackPrcng1270BackendEV(ctx), // prcng-1270_v1_mc_pro_promo
  ])

  const data = await getPageData({
    criticalPromises: {
      metadata: getCourseMeta(ctx, { slug }),
    },
    nonCriticalPromises: {
      annualPass: getDefaultProduct(ctx),
      trial: getFreeTrialByToken(ctx, { referralToken }),
      categories: getV2Categories(ctx),
      classCategories: getGrowthClassCategories(ctx, slug),
      otherClassesInSameSubcategories: getGrowthClassesBySubcategoryIds(
        ctx,
        classSubcategories.map((subcat) => subcat.id),
      ),
      mobileContentGems: getMobileContentGems(ctx, { id: course.id }),
      marketingRows: getPen7401MarketingRows(ctx),
    },
    propsMapper: (restProps) => ({
      course,
      savedDynamicHero,
      ctaNextPage,
      primaryCtaOverride,
      classSubcategories,
      primaryGrowthCatAndSubcat,
      instructorQuote: getEligibleInstructorQuote(course), // pen-7315_v1
      ...restProps,
    }),
  })

  return transformCourseRedirect(slug, data, resolvedUrl)
}

export default Course
